import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Building2, Search, Star, Clock, Loader2, X, Keyboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Company } from '@/types';
import { logger } from '@/utils/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/use-debounce';

interface CompanyWithMetadata extends Company {
  isFavorite?: boolean;
  lastAccessedAt?: string;
}

interface CompanySwitcherProps {
  disabled?: boolean;
  onSwitch?: (companyId: string) => void;
}

// Helper to estimate item height
const ITEM_HEIGHT = 52; // Height of each company item
const SECTION_HEADER_HEIGHT = 28; // Height of section headers

export const VirtualizedCompanySwitcher: React.FC<CompanySwitcherProps> = ({ 
  disabled = false,
  onSwitch 
}) => {
  const { currentCompany, companies, switchCompany } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  
  // Debounce search for performance
  const debouncedSearch = useDebounce(search, 300);

  // Fetch user preferences from database
  const { data: preferences, isLoading: preferencesLoading } = useQuery({
    queryKey: ['companyPreferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return { favorites: [], recent: [] };
      
      const [favoritesRes, recentRes] = await Promise.all([
        supabase.rpc('get_favorite_companies', { p_user_id: user.id }),
        supabase.rpc('get_recent_companies', { p_user_id: user.id, p_limit: 5 })
      ]);
      
      if (favoritesRes.error) {
        logger.error('Failed to fetch favorite companies', favoritesRes.error);
      }
      if (recentRes.error) {
        logger.error('Failed to fetch recent companies', recentRes.error);
      }
      
      return {
        favorites: favoritesRes.data?.map(d => d.company_id) || [],
        recent: recentRes.data?.map(d => ({
          id: d.company_id,
          lastAccessedAt: d.last_accessed_at
        })) || []
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to update last accessed
  const updateLastAccessedMutation = useMutation({
    mutationFn: async (companyId: string) => {
      if (!user?.id) return;
      
      const { error } = await supabase.rpc('update_company_last_accessed', {
        p_user_id: user.id,
        p_company_id: companyId
      });
      
      if (error) {
        logger.error('Failed to update last accessed', error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyPreferences'] });
    }
  });

  // Mutation to toggle favorite
  const toggleFavoriteMutation = useMutation({
    mutationFn: async (companyId: string) => {
      if (!user?.id) return false;
      
      const { data, error } = await supabase.rpc('toggle_company_favorite', {
        p_user_id: user.id,
        p_company_id: companyId
      });
      
      if (error) {
        logger.error('Failed to toggle favorite', error);
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyPreferences'] });
    }
  });

  // Process companies with metadata
  const companiesWithMetadata = useMemo(() => {
    if (!preferences) return companies;
    
    return companies.map(company => {
      const recentEntry = preferences.recent.find(r => r.id === company.id);
      return {
        ...company,
        isFavorite: preferences.favorites.includes(company.id),
        lastAccessedAt: recentEntry?.lastAccessedAt
      } as CompanyWithMetadata;
    });
  }, [companies, preferences]);

  // Filter and sort companies
  const filteredCompanies = useMemo(() => {
    const searchLower = debouncedSearch.toLowerCase();
    
    // Filter by search
    let filtered = companiesWithMetadata;
    if (searchLower) {
      filtered = companiesWithMetadata.filter(company => 
        company.name.toLowerCase().includes(searchLower) ||
        company.website_url?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort by: current company first, then favorites, then recent, then alphabetical
    return filtered.sort((a, b) => {
      // Current company always first
      if (a.id === currentCompany?.id) return -1;
      if (b.id === currentCompany?.id) return 1;
      
      // Favorites next
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      
      // Recent companies after favorites
      if (a.lastAccessedAt && !b.lastAccessedAt) return -1;
      if (!a.lastAccessedAt && b.lastAccessedAt) return 1;
      if (a.lastAccessedAt && b.lastAccessedAt) {
        return b.lastAccessedAt.localeCompare(a.lastAccessedAt);
      }
      
      // Alphabetical for the rest
      return a.name.localeCompare(b.name);
    });
  }, [companiesWithMetadata, debouncedSearch, currentCompany]);

  // Create virtualized list items with sections
  const virtualItems = useMemo(() => {
    const items: { type: 'header' | 'company'; data: string | CompanyWithMetadata; height: number }[] = [];
    const favorites = filteredCompanies.filter(c => c.isFavorite);
    const recent = filteredCompanies.filter(c => !c.isFavorite && c.lastAccessedAt).slice(0, 5);
    const others = filteredCompanies.filter(c => !c.isFavorite && !recent.includes(c));
    
    // Add sections
    if (favorites.length > 0) {
      items.push({ type: 'header', data: 'Favorites', height: SECTION_HEADER_HEIGHT });
      favorites.forEach(company => {
        items.push({ type: 'company', data: company, height: ITEM_HEIGHT });
      });
    }
    
    if (recent.length > 0) {
      items.push({ type: 'header', data: 'Recent', height: SECTION_HEADER_HEIGHT });
      recent.forEach(company => {
        items.push({ type: 'company', data: company, height: ITEM_HEIGHT });
      });
    }
    
    if (others.length > 0) {
      items.push({ type: 'header', data: 'All Companies', height: SECTION_HEADER_HEIGHT });
      others.forEach(company => {
        items.push({ type: 'company', data: company, height: ITEM_HEIGHT });
      });
    }
    
    return items;
  }, [filteredCompanies]);

  // Setup virtualizer
  const virtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => virtualItems[index]?.height || ITEM_HEIGHT,
    overscan: 5,
  });

  // Calculate selected company index in filtered list
  const selectedCompanyIndex = useMemo(() => {
    let companyIndex = 0;
    for (let i = 0; i < virtualItems.length; i++) {
      if (virtualItems[i].type === 'company') {
        if (companyIndex === selectedIndex) {
          return i;
        }
        companyIndex++;
      }
    }
    return -1;
  }, [virtualItems, selectedIndex]);

  // Handle company selection
  const handleSelect = useCallback(async (company: CompanyWithMetadata) => {
    if (company.id === currentCompany?.id) {
      setOpen(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Update last accessed in database
      await updateLastAccessedMutation.mutateAsync(company.id);
      
      // Call custom handler if provided
      if (onSwitch) {
        await onSwitch(company.id);
      } else {
        // Default behavior
        switchCompany(company.id);
        navigate(`/w/${company.slug}/dashboard`);
      }
    } catch (error) {
      logger.error('Failed to switch company', error);
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  }, [currentCompany, updateLastAccessedMutation, onSwitch, switchCompany, navigate]);

  // Handle favorite toggle
  const handleToggleFavorite = useCallback(async (company: CompanyWithMetadata, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await toggleFavoriteMutation.mutateAsync(company.id);
    } catch (error) {
      logger.error('Failed to toggle favorite', error);
    }
  }, [toggleFavoriteMutation]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) return;
    
    const companyCount = virtualItems.filter(item => item.type === 'company').length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % companyCount);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + companyCount) % companyCount);
        break;
      case 'Enter': {
        e.preventDefault();
        const selectedItem = virtualItems[selectedCompanyIndex];
        if (selectedItem?.type === 'company') {
          handleSelect(selectedItem.data as CompanyWithMetadata);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
    }
  }, [open, virtualItems, selectedCompanyIndex, handleSelect]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedSearch]);

  // Focus search input when opened
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedCompanyIndex >= 0) {
      virtualizer.scrollToIndex(selectedCompanyIndex, { align: 'center' });
    }
  }, [selectedCompanyIndex, virtualizer]);

  if (!currentCompany) return null;

  const showShortcuts = companies.length >= 20;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="font-medium">Switching...</span>
            </>
          ) : (
            <>
              <span className="font-medium">{currentCompany.name}</span>
              {companies.length > 1 && (
                <span className="text-xs text-muted-foreground ml-1">
                  ({companies.length})
                </span>
              )}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0"
        align="start"
        onKeyDown={handleKeyDown}
      >
        {/* Search Header */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {search && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearch('')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          {/* Keyboard shortcuts hint */}
          {showShortcuts && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Keyboard className="h-3 w-3" />
              <span>Use ↑↓ to navigate, Enter to select</span>
            </div>
          )}
        </div>

        {/* Virtualized Companies List */}
        <div
          ref={parentRef}
          className="h-[400px] overflow-auto"
        >
          {preferencesLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : virtualItems.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              No companies found
            </div>
          ) : (
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const item = virtualItems[virtualItem.index];
                const isSelected = virtualItem.index === selectedCompanyIndex;
                
                if (item.type === 'header') {
                  return (
                    <div
                      key={virtualItem.key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-background sticky top-0 z-10"
                    >
                      {item.data}
                    </div>
                  );
                } else {
                  const company = item.data as CompanyWithMetadata;
                  const isCurrent = company.id === currentCompany.id;
                  
                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <div
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent h-full',
                          isSelected && 'bg-accent',
                          isCurrent && 'font-medium'
                        )}
                        onClick={() => handleSelect(company)}
                      >
                        <Building2 className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{company.name}</div>
                          {company.lastAccessedAt && !company.isFavorite && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>Recently used</span>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            'h-6 w-6 p-0',
                            !company.isFavorite && 'opacity-0 hover:opacity-100',
                            'transition-opacity'
                          )}
                          onClick={(e) => handleToggleFavorite(company, e)}
                          disabled={toggleFavoriteMutation.isPending}
                        >
                          <Star
                            className={cn(
                              'h-3.5 w-3.5',
                              company.isFavorite
                                ? 'fill-yellow-500 text-yellow-500'
                                : 'text-muted-foreground'
                            )}
                          />
                        </Button>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground"
            onClick={() => {
              setOpen(false);
              navigate('/companies');
            }}
          >
            Manage Companies
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};